import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export type VisiteStatut = "PROGRAMMEE" | "EFFECTUEE" | "ANNULEE" | "A_REPROGRAMMER";
/**
 * Motif (situation du fidèle). CONVALESCENCE et SOINS_PALLIATIFS depuis le
 * 10/10/2026 : avant, ces statuts du formulaire devenaient « AUTRE ».
 */
export type VisiteMotif =
  | "MALADE" | "HOSPITALISE" | "CONVALESCENCE" | "SOINS_PALLIATIFS"
  | "PERSONNE_AGEE" | "DEUIL" | "EUCHARISTIE_DOMICILE" | "AUTRE";

export interface VisitePastorale {
  id: string;
  parishId: string;
  parishionerId?: string;
  fideleNom: string;
  fideleAge?: number;
  fideleAdresse?: string;
  fidelePhone?: string;
  motif: VisiteMotif;
  dateDemande: string;
  dateVisitePrevue?: string;
  dateVisiteEffectuee?: string;
  statut: VisiteStatut;
  visiteurNom?: string;
  observations?: string;
  sacrementApporte?: string;
  frequenceSouhaitee?: string;
  /** Demande urgente : en rouge, en haut de la liste (manuel 2, action 15). */
  urgente?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface VisitesEnvelope {
  items: VisitePastorale[];
  page: number;
  size: number;
  total: number;
}

export function useVisitesPastorales(parishId: string | null | undefined, opts: {
  statut?: VisiteStatut; motif?: VisiteMotif;
  from?: string; to?: string; page?: number; size?: number;
} = {}) {
  return useQuery({
    queryKey: ["visites-pastorales", parishId, opts],
    enabled: !!parishId,
    queryFn: async () => {
      const params = new URLSearchParams({ parishId: String(parishId) });
      if (opts.statut) params.append("statut", opts.statut);
      if (opts.motif) params.append("motif", opts.motif);
      if (opts.from) params.append("from", opts.from);
      if (opts.to) params.append("to", opts.to);
      if (opts.page !== undefined) params.append("page", String(opts.page));
      if (opts.size !== undefined) params.append("size", String(opts.size));
      const res = await apiRequest("GET", `/api/micro/visites-pastorales?${params}`);
      return (await res.json()) as VisitesEnvelope;
    },
  });
}

export function useCreateVisitePastorale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Omit<VisitePastorale, "id" | "createdAt" | "updatedAt">) => {
      const res = await apiRequest("POST", "/api/micro/visites-pastorales", body);
      return (await res.json()) as VisitePastorale;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["visites-pastorales"] }),
  });
}

/** Mise à jour d'une visite (PUT /visites-pastorales/{id}) : planning (date prévue, visiteur). */
export function useUpdateVisitePastorale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Partial<VisitePastorale> }) => {
      const res = await apiRequest("PUT", `/api/micro/visites-pastorales/${id}`, body);
      return (await res.json()) as VisitePastorale;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["visites-pastorales"] }),
  });
}

export function useEffectuerVisite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body?: Partial<VisitePastorale> }) => {
      const res = await apiRequest("PUT", `/api/micro/visites-pastorales/${id}/effectuer`, body ?? {});
      return (await res.json()) as VisitePastorale;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["visites-pastorales"] }),
  });
}
